import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Loader2,
  Save,
  X,
  AlertCircle,
  BookOpen,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { apiPost } from '@/utils/api';

interface ElementInput {
  id: string;
  name: string;
  weight: number;
  volumeHoraire: number | null;
}

interface ModuleInput {
  id: string;
  code: string;
  name: string;
  coefficient: number;
  elements: ElementInput[];
  isExpanded: boolean;
}

interface BulkModuleEditorProps {
  semesterId: string;
  semesterNumber: number;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

// Generate unique ID for local state management
const generateId = () => Math.random().toString(36).substr(2, 9);

// Create empty module
const createEmptyModule = (): ModuleInput => ({
  id: generateId(),
  code: '',
  name: '',
  coefficient: 1,
  elements: [{ id: generateId(), name: '', weight: 100, volumeHoraire: null }],
  isExpanded: true,
});

// Create empty element
const createEmptyElement = (): ElementInput => ({
  id: generateId(),
  name: '',
  weight: 0,
  volumeHoraire: null,
});

const BulkModuleEditor: React.FC<BulkModuleEditorProps> = ({
  semesterId,
  semesterNumber,
  onClose,
  onSuccess,
  onError,
}) => {
  const [modules, setModules] = useState<ModuleInput[]>([createEmptyModule()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add a new module row
  const addModule = () => {
    setModules((prev) => [...prev, createEmptyModule()]);
  };

  // Remove a module
  const removeModule = (moduleId: string) => {
    if (modules.length === 1) {
      setError('Vous devez avoir au moins un module');
      return;
    }
    setModules((prev) => prev.filter((m) => m.id !== moduleId));
  };

  // Update a module field
  const updateModule = (moduleId: string, field: keyof ModuleInput, value: string | number | boolean) => {
    setModules((prev) =>
      prev.map((m) =>
        m.id === moduleId ? { ...m, [field]: value } : m
      )
    );
  };

  // Toggle module expansion
  const toggleModuleExpansion = (moduleId: string) => {
    setModules((prev) =>
      prev.map((m) =>
        m.id === moduleId ? { ...m, isExpanded: !m.isExpanded } : m
      )
    );
  };

  // Add element to a module
  const addElement = (moduleId: string) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        const newElements = [...m.elements, createEmptyElement()];
        // Redistribute weights equally
        const weightPerElement = Math.floor(100 / newElements.length);
        const remainder = 100 - weightPerElement * newElements.length;
        return {
          ...m,
          elements: newElements.map((el, idx) => ({
            ...el,
            weight: weightPerElement + (idx === 0 ? remainder : 0),
          })),
        };
      })
    );
  };

  // Remove element from a module
  const removeElement = (moduleId: string, elementId: string) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        if (m.elements.length <= 1) return m; // Keep at least one element
        const newElements = m.elements.filter((el) => el.id !== elementId);
        // Redistribute weights
        const weightPerElement = Math.floor(100 / newElements.length);
        const remainder = 100 - weightPerElement * newElements.length;
        return {
          ...m,
          elements: newElements.map((el, idx) => ({
            ...el,
            weight: weightPerElement + (idx === 0 ? remainder : 0),
          })),
        };
      })
    );
  };

  // Update element field
  const updateElement = (
    moduleId: string,
    elementId: string,
    field: keyof ElementInput,
    value: string | number | null
  ) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        return {
          ...m,
          elements: m.elements.map((el) =>
            el.id === elementId ? { ...el, [field]: value } : el
          ),
        };
      })
    );
  };

  // Update all element weights for a module
  const updateElementWeight = (moduleId: string, elementId: string, newWeight: number) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        return {
          ...m,
          elements: m.elements.map((el) =>
            el.id === elementId ? { ...el, weight: Math.max(0, Math.min(100, newWeight)) } : el
          ),
        };
      })
    );
  };

  // Validate the form
  const validateForm = (): string | null => {
    for (let i = 0; i < modules.length; i++) {
      const m = modules[i];
      if (!m.code.trim()) return `Module ${i + 1}: Le code est requis`;
      if (!m.name.trim()) return `Module ${i + 1}: Le nom est requis`;
      if (m.coefficient < 0.1) return `Module ${i + 1}: Le coefficient doit être >= 0.1`;

      // Check elements
      const validElements = m.elements.filter((el) => el.name.trim());
      if (validElements.length === 0) {
        return `Module ${i + 1}: Au moins un élément est requis`;
      }

      // Check weights sum to 100
      const totalWeight = validElements.reduce((sum, el) => sum + el.weight, 0);
      if (Math.abs(totalWeight - 100) > 0.1) {
        return `Module ${i + 1}: Les poids des éléments doivent totaliser 100% (actuellement: ${totalWeight}%)`;
      }
    }
    return null;
  };

  // Submit all modules
  const handleSubmit = async () => {
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the bulk data
      const bulkData = {
        semesterId,
        modules: modules.map((m) => ({
          code: m.code.trim(),
          name: m.name.trim(),
          coefficient: m.coefficient,
          elements: m.elements
            .filter((el) => el.name.trim())
            .map((el) => ({
              name: el.name.trim(),
              weight: el.weight,
              volumeHoraire: el.volumeHoraire,
            })),
        })),
      };

      await apiPost('/filieres/modules/bulk', bulkData);

      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la création';
      setError(message);
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate total weight for a module
  const getTotalWeight = (elements: ElementInput[]) => {
    return elements.filter((el) => el.name.trim()).reduce((sum, el) => sum + el.weight, 0);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-blue-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Modules du Semestre {semesterNumber}
                </h2>
                <p className="text-sm text-gray-500">
                  Ajoutez tous les modules et leurs éléments
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {modules.map((module, moduleIndex) => (
                <div
                  key={module.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Module Header Row */}
                  <div className="bg-gray-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      {/* Module Number */}
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-primary-700">
                          {moduleIndex + 1}
                        </span>
                      </div>

                      {/* Code */}
                      <div className="w-28">
                        <input
                          type="text"
                          value={module.code}
                          onChange={(e) => updateModule(module.id, 'code', e.target.value)}
                          placeholder="Code"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Name */}
                      <div className="flex-1">
                        <input
                          type="text"
                          value={module.name}
                          onChange={(e) => updateModule(module.id, 'name', e.target.value)}
                          placeholder="Nom du module"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Coefficient */}
                      <div className="w-20">
                        <input
                          type="number"
                          value={module.coefficient}
                          onChange={(e) =>
                            updateModule(module.id, 'coefficient', parseFloat(e.target.value) || 1)
                          }
                          placeholder="Coef"
                          step="0.5"
                          min="0.1"
                          max="10"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleModuleExpansion(module.id)}
                          className="p-1.5 text-gray-500 hover:bg-gray-200 rounded transition-colors"
                          title="Voir les éléments"
                        >
                          {module.isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => removeModule(module.id)}
                          className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors"
                          title="Supprimer le module"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Elements Section */}
                  {module.isExpanded && (
                    <div className="px-4 py-3 bg-white border-t border-gray-100">
                      <div className="pl-11 space-y-2">
                        {/* Elements Header */}
                        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium mb-2">
                          <span className="flex-1">Éléments de module</span>
                          <span className="w-20 text-center">Poids (%)</span>
                          <span className="w-24 text-center">Vol. Horaire (h)</span>
                          <span className="w-8"></span>
                        </div>

                        {/* Element Rows */}
                        {module.elements.map((element, elementIndex) => (
                          <div key={element.id} className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-medium text-gray-500">
                                {elementIndex + 1}
                              </span>
                            </div>
                            <input
                              type="text"
                              value={element.name}
                              onChange={(e) =>
                                updateElement(module.id, element.id, 'name', e.target.value)
                              }
                              placeholder={`Élément ${elementIndex + 1}`}
                              className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                            <input
                              type="number"
                              value={element.weight}
                              onChange={(e) =>
                                updateElementWeight(
                                  module.id,
                                  element.id,
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              min="0"
                              max="100"
                              className="w-20 px-2 py-1.5 text-sm text-center border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                            <input
                              type="number"
                              value={element.volumeHoraire ?? ''}
                              onChange={(e) =>
                                updateElement(
                                  module.id,
                                  element.id,
                                  'volumeHoraire',
                                  e.target.value ? parseFloat(e.target.value) : null
                                )
                              }
                              min="0"
                              step="0.5"
                              placeholder="Ex: 30"
                              className="w-24 px-2 py-1.5 text-sm text-center border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                            <button
                              onClick={() => removeElement(module.id, element.id)}
                              disabled={module.elements.length <= 1}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Supprimer l'élément"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}

                        {/* Add Element Button & Weight Total */}
                        <div className="flex items-center justify-between pt-2">
                          <button
                            onClick={() => addElement(module.id)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                            Ajouter un élément
                          </button>
                          <div
                            className={`text-xs font-medium px-2 py-1 rounded ${
                              Math.abs(getTotalWeight(module.elements) - 100) < 0.1
                                ? 'text-green-600 bg-green-50'
                                : 'text-amber-600 bg-amber-50'
                            }`}
                          >
                            Total: {getTotalWeight(module.elements)}%
                            {Math.abs(getTotalWeight(module.elements) - 100) > 0.1 && (
                              <span className="ml-1">(doit être 100%)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Module Button */}
            <button
              onClick={addModule}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors border-2 border-dashed border-primary-200"
            >
              <Plus className="w-5 h-5" />
              Ajouter un module
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-500">
              {modules.length} module{modules.length > 1 ? 's' : ''} à créer
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer tous les modules
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkModuleEditor;
